import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useUrlAndHashFromPath } from '@/hooks/useUrlAndHashFromPath';

const previousHashByKey = new Map<string, string | undefined>();

export function getRouteHash(
  hash: string | undefined,
  modalHash: string
): string | undefined {
  if (hash === modalHash) {
    return previousHashByKey.get(modalHash);
  }
  return hash;
}

export const useToggleOpenState = (
  defaultValue?: boolean | string,
  onToggle?: () => void
): [
  boolean,
  (v?: boolean | React.MouseEvent<HTMLElement>) => void,
  () => void,
  () => void
] => {
  const { push, replace } = useRouter();
  const { url, hash } = useUrlAndHashFromPath();
  const isBasedOnHash = typeof defaultValue === 'string';
  const [mounted, setMounted] = useState(false);
  const [isOpenState, setIsOpenState] = useState(
    isBasedOnHash ? hash === defaultValue : !!defaultValue
  );

  const isOpen = mounted
    ? isBasedOnHash
      ? hash === defaultValue
      : isOpenState
    : false;

  async function openHash() {
    if (typeof defaultValue !== 'string') return;
    if (hash !== defaultValue) {
      previousHashByKey.set(defaultValue, hash);
    }
    await push(`${url}#${defaultValue}`, undefined, {
      scroll: false,
    });
  }

  async function closeHash() {
    if (typeof defaultValue !== 'string') return;
    const previousHash = previousHashByKey.get(defaultValue);
    previousHashByKey.delete(defaultValue);
    await replace(previousHash ? `${url}#${previousHash}` : url, undefined, {
      scroll: false,
    });
  }

  function toggle(v?: boolean | React.MouseEvent<HTMLElement>) {
    onToggle?.();
    if (isBasedOnHash) {
      if (hash === defaultValue || v === false) {
        closeHash();
      } else {
        openHash();
      }
      return;
    }
    setIsOpenState(prev => (typeof v === 'boolean' ? v : !prev));
  }

  function open() {
    onToggle?.();
    if (isBasedOnHash) {
      openHash();
      return;
    }
    setIsOpenState(true);
  }

  function close() {
    onToggle?.();
    if (isBasedOnHash) {
      closeHash();
      return;
    }
    setIsOpenState(false);
  }

  useEffect(() => {
    setMounted(true);
  }, []);

  return [isOpen, toggle, open, close];
};
